import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './style.scss';
import MenuItem from '@material-ui/core/MenuItem';
import List from '@material-ui/core/List';
import BookingConcernPicker from 'components/HealthRecords/Appointments/BookingConcernPicker';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DocumentDetails from 'components/HealthRecords/Documents/DocumentDetails';
import SelectableListItem from 'components/Utils/SelectableListItem';
import DocumentBadge from 'components/HealthRecords/Documents/DocumentBadge';
import PerformanceUtils from 'services/PerformanceUtils';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import { PatientInformation } from 'scenes/HomeStaff/PatientDirectory/PatientSearchScreen';
import RouteUtils from 'services/RouteUtils';
import usePersistedState from 'hooks/usePersistedState';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { patientActions } from 'careand-redux/slices/patients';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import SubscriptionUtils from 'services/SubscriptionUtils';
import { DefaultSwitch } from 'services/UIUtils';
import ResponsiveColumns from 'components/ResponsiveColumns';
import CPPChat from 'components/Patient/CPPChat';
import DocumentUtils from 'services/DocumentUtils';
import { getTenantIdentity } from 'hooks/useTenantIdentity';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';
import { ActivePhysician } from 'careand-redux/selectors';
import Icon from 'components/Utils/NewIcon';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';

const DEFAULT_OBJECT = {};

const BatchApproveDocuments = (props) => {
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);
  const patientId = props.patientId;
  const ownPhysician = useAppSelector(ActivePhysician);
  const patient = useAppSelector((state) => state.patients.byId[patientId]);
  const isIntern = ownPhysician?.intern_of;
  const patientSubscription = useAppSelector((state) => state.payments.subscriptions[patientId]);
  const [bookingConcernId, setBookingConcernId] = useState();

  useEffect(() => {
    if (patientId && !patientSubscription) {
      dispatch(api.Payments.getUserSubscription({ userId: patientId }));
    }
  }, [dispatch, patientId, patientSubscription]);

  const isSubscriber = !SubscriptionUtils.isUnsubscribed(patientSubscription);

  const key = useMemo(() => getPersistedKey(props.documents), [props.documents]);

  const keyChanged = `${key}_changed`;
  const keyMessage = `${key}_message`;
  const keyActions = `${key}_actions`;
  const keyFollowUp = `${key}_follow_up`;

  const [changedDocuments, setChangedDocuments, deleteStateChanged] = usePersistedState(
    keyChanged,
    props.documents,
    true,
  );

  const [message, setMessage, deleteStateMessage] = usePersistedState(keyMessage, '');
  const [followUp, setFollowUp] = usePersistedState(keyFollowUp, false);
  const [actions, setActions, deleteStateActions] = usePersistedState(keyActions, DEFAULT_OBJECT);

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(props.documents[0]?.id);
  const [openCPP, setOpenCPP] = useState(false);

  const navigateTo = useRef();

  const selectedDocument = useMemo(() => {
    return changedDocuments.find((document) => document.id === selected);
  }, [changedDocuments, selected]);

  useEffect(() => {
    if (props.documents.length > changedDocuments.length) {
      let changedIds = changedDocuments.map((doc) => doc.id);
      let newDoc = props.documents.find((doc) => !changedIds.includes(doc.id));
      if (newDoc) {
        setChangedDocuments([...changedDocuments, newDoc].filter((doc) => doc.physician_id));
      } else {
        setChangedDocuments([...changedDocuments].filter((doc) => doc.physician_id));
      }
    } else if (props.documents.length < changedDocuments.length) {
      setChangedDocuments((prevState) => {
        return prevState.filter((doc) => !!props.documents.find(({ id }) => id === doc.id));
      });
    } else {
      let docs = {};
      props.documents.forEach((doc) => {
        docs[doc.id] = docs[doc.id] || {};
        docs[doc.id].notOwn = doc;
      });
      changedDocuments.forEach((doc) => {
        docs[doc.id] = docs[doc.id] || {};
        docs[doc.id].own = doc;
      });
      let changed = false;
      let result = Object.keys(docs).map((id) => {
        let own = docs[id].own || {};
        let notOwn = docs[id].notOwn || {};
        if (own.date_updated < notOwn.date_updated) {
          changed = true;
          if (notOwn.title !== own.title) {
            own = { ...own, title: notOwn.title };
          }
          if (notOwn.medications !== own.medications) {
            own = { ...own, medications: notOwn.medications };
          }
          if (notOwn.analysis !== own.analysis) {
            own = { ...own, analysis: notOwn.analysis };
          }
          if (notOwn.pharmacy_authorization_analysis !== own.pharmacy_authorization_analysis) {
            own = {
              ...own,
              pharmacy_authorization_analysis: notOwn.pharmacy_authorization_analysis,
            };
          }
          if (notOwn.comments !== own.comments) {
            own = { ...own, comments: notOwn.comments };
          }
          if (notOwn.general_comments !== own.general_comments) {
            own = { ...own, general_comments: notOwn.general_comments };
          }
          if (notOwn.document_type !== own.document_type) {
            own = { ...own, document_type: notOwn.document_type };
          }
          if (notOwn.filename !== own.filename) {
            own = { ...own, filename: notOwn.filename };
          }
          own = { ...own, date_updated: notOwn.date_updated };
        }
        return own;
      });
      if (changed) {
        setChangedDocuments(result);
      }
    }
  }, [props.documents, changedDocuments, selected]);

  function clearLocalCache() {
    deleteStateChanged();
    deleteStateMessage();
    deleteStateActions();
  }

  function hide(document) {
    let newActions = {
      ...actions,
      [document.id]: ACTIONS.hide,
    };
    setActions(newActions);
    selectNextDocument(newActions);
  }

  function show(document) {
    let newActions = {
      ...actions,
      [document.id]: ACTIONS.show,
    };
    setActions(newActions);
    selectNextDocument(newActions);
  }

  function selectNextDocument(actions) {
    const todo = changedDocuments.filter((document) => !actions[document.id]);
    let id = undefined;
    if (todo.length > 0) id = todo[0].id;

    setSelected(id);
  }

  async function saveChanges() {
    setLoading(true);

    let documents = changedDocuments
      .filter((document) => !!actions[document.id])
      .map((document) => {
        return {
          ...document,
          approved: true,
          hide: actions[document.id] === ACTIONS.hide,
        };
      });

    let results = await dispatch(
      api.Documents.approveDocuments({
        approvePatientDocuments: {
          documents,
          message,
          follow_up: followUp,
          booking_concern_id: bookingConcernId ?? undefined,
        },
      }),
      {
        errorTitle: 'Approve Documents',
      },
    );

    if (results) {
      let doneIds = documents.map(({ id }) => id);
      setChangedDocuments(changedDocuments.filter((doc) => !doneIds.includes(doc.id)));
      setActions({});
      clearLocalCache();
      dispatch(patientActions.patientOpened(props.patientId));
    }

    setLoading(false);
  }

  function onDocumentChange(document) {
    let newDocuments = PerformanceUtils.editOrAdd(document, changedDocuments);
    setChangedDocuments(newDocuments);
  }

  function onViewProfile(patient) {
    if (patient) {
      let route = RouteUtils.getRoute(RouteUtils.routes().patient, {
        ':patientId': patient.id,
      });
      nav.push(route);
    }
  }

  async function archiveDocument(document) {
    document = {
      ...document,
      archived: true,
      approved: true,
    };

    setLoading(true);
    let result = await dispatch(api.Documents.updateDocument({ patientDocument: document }), {
      errorTitle: 'Archive Document',
    });
    setLoading(false);
    if (result) {
      let newDocuments = PerformanceUtils.removeFromArray(document, changedDocuments);
      setChangedDocuments(newDocuments);
    }
  }

  let leftSideClassName = 'left-side full-height-panel underside-right-panel';
  if (openCPP) leftSideClassName += ' small';

  return (
    <div className="batch-approve-documents full-height">
      <div className="row full-width full-height list-with-details responsive-directory">
        <ResponsiveColumns
          navigateTo={navigateTo}
          columns={[
            {
              name: 'Documents',
              element: (
                <div className={leftSideClassName}>
                  <div
                    className="left-side-list scroll-list list"
                    style={isIntern ? { height: '100%' } : undefined}
                  >
                    <div className="button-wrapper">
                      <DefaultButton
                        name={openCPP ? 'Hide CPP' : 'Show CPP'}
                        className="blue"
                        icon={openCPP ? 'eye-slash' : 'eye'}
                        onClick={() => setOpenCPP(!openCPP)}
                      />
                    </div>
                    <AllLists
                      documents={changedDocuments}
                      actions={actions}
                      patient={patient}
                      selected={selected}
                      onSelect={(document) => {
                        setSelected(document.id);
                        setTimeout(() => {
                          navigateTo.current?.(1);
                        });
                      }}
                    />
                  </div>
                  {!isIntern && changedDocuments?.length > 0 && (
                    <ApproveSection
                      documents={changedDocuments}
                      actions={actions}
                      loading={loading}
                      message={message}
                      setMessage={setMessage}
                      onSave={saveChanges}
                      isSubscriber={isSubscriber}
                      followUp={followUp}
                      setFollowUp={setFollowUp}
                      onClose={props.onClose}
                      bookingConcernId={bookingConcernId}
                      setBookingConcernId={setBookingConcernId}
                    />
                  )}
                </div>
              ),
            },
            {
              name: 'Detail',
              element: !selectedDocument ? (
                openCPP ? (
                  <div className="approve-documents-cpp-wrapper">
                    <CPPChat patient={patient} defaultHidePanel={true} />
                  </div>
                ) : (
                  <PatientInformation patient={patient} onViewProfile={onViewProfile} />
                )
              ) : (
                <DocumentDetails
                  key={selectedDocument?.id + selectedDocument?.date_updated}
                  patient={patient}
                  document={selectedDocument}
                  disableEdit={false}
                  disableApprove={true}
                  allowExpand={true}
                  allowDelete={false}
                  autoLoad={true}
                  disableSend={false}
                  onDocumentChange={onDocumentChange}
                  allowPin={false}
                  openCPP={openCPP}
                  onClose={props.onClose}
                  extraButtons={
                    !isIntern && (
                      <>
                        <div className="row" style={{ flexGrow: 0 }}>
                          <div className="inline-block">
                            <ConfirmationButton
                              name="Archive"
                              icon="box-archive"
                              onClick={() => archiveDocument(selectedDocument)}
                              disabled={loading}
                              confirmation={{
                                message: 'Are you sure you want to archive this document?',
                              }}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="inline-block">
                            <DefaultButton
                              name="Hide"
                              className="red"
                              icon="eye-slash"
                              onClick={() => hide(selectedDocument)}
                              disabled={loading}
                            />
                          </div>
                          <div className="inline-block">
                            <DefaultButton
                              name="Show"
                              className="blue"
                              icon="eye"
                              onClick={() => show(selectedDocument)}
                              disabled={loading}
                            />
                          </div>
                        </div>
                      </>
                    )
                  }
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

const ApproveSection = ({
  documents = [],
  actions = {},
  loading,
  message,
  setMessage,
  onSave,
  isSubscriber,
  followUp,
  setFollowUp,
  bookingConcernId,
  setBookingConcernId,
}) => {
  const key = useMemo(() => getPersistedKey(documents), [documents]);
  const patientId = useMemo(() => {
    return documents.find((it) => it.patient_id)?.patient_id;
  }, [documents]);

  const keyTemplate = `${key}_template`;

  const [messageTemplate, setMessageTemplate] = usePersistedState(
    keyTemplate,
    FOLLOW_UP_MESSAGES[0],
  );

  let warning = '';
  let documentsDone = Object.values(actions).filter((action) => !!action).length;
  let buttonDisabled = documentsDone === 0;

  if (documentsDone !== documents.length)
    warning = `${documents.length - documentsDone} documents uncategorized`;

  const showFollowUp = Object.values(actions).find((action) => action === 'hide');

  return (
    <div className="approve-section">
      {showFollowUp && (
        <DefaultSwitch
          name="follow_up_task"
          onChange={(event, isInputChecked) => {
            setFollowUp(isInputChecked);
          }}
          label="Create Follow-up Task"
          padded={false}
          reversed={true}
          checked={followUp}
        />
      )}
      <div className="text-message-warning">
        <Icon fontAwesome="circle-info" width="16px" height="16px" />
        <span>Text messages should not contain PHI.</span>
      </div>
      <DefaultSelectField
        label="Patient Text Message"
        style={{ width: '100%' }}
        value={messageTemplate.id}
        onChange={(event) => {
          let template = FOLLOW_UP_MESSAGES.find((template) => template.id === event.target.value);
          setMessageTemplate(template);
          setMessage(template.text);
          if (!template.id.startsWith('followup_')) setBookingConcernId(null);
        }}
        inputElements={[
          ...FOLLOW_UP_MESSAGES.filter(({ subscriber }) => !subscriber || isSubscriber).map(
            (messageTemplate) => (
              <MenuItem value={messageTemplate.id} key={'select-' + messageTemplate.id}>
                {messageTemplate.value}
              </MenuItem>
            ),
          ),
        ]}
      />
      {messageTemplate.id.startsWith('followup_') && (
        <BookingConcernPicker
          patientId={patientId}
          value={bookingConcernId}
          onChange={setBookingConcernId}
        />
      )}
      <TextareaAutosize
        name="Patient Text Message"
        value={message}
        disabled={messageTemplate.disabled}
        onChange={(event) => setMessage(event.target.value)}
      />
      <DefaultButton
        name={`Approve ${documentsDone} Documents`}
        disabled={buttonDisabled}
        loading={loading}
        style={{ width: '100%' }}
        className="blue"
        onClick={onSave}
      />
      <span className="warning">{warning}</span>
    </div>
  );
};

const AllLists = ({ documents = [], actions, patient, selected, onSelect }) => {
  const todo = documents.filter((document) => !actions[document.id]);
  const hide = documents.filter((document) => actions[document.id] === ACTIONS.hide);
  const show = documents.filter((document) => actions[document.id] === ACTIONS.show);

  return (
    <div>
      <MyList
        title="Uncategorized"
        documents={todo}
        selected={selected}
        patient={patient}
        onSelect={onSelect}
      />
      <MyList
        title="Hide"
        documents={hide}
        selected={selected}
        patient={patient}
        onSelect={onSelect}
      />
      <MyList
        title="Show"
        documents={show}
        selected={selected}
        patient={patient}
        onSelect={onSelect}
      />
    </div>
  );
};

const MyList = ({ title, documents, selected, patient, onSelect }) =>
  documents.length > 0 && (
    <div>
      <span className="small-title padded-title">{title}</span>
      <List>
        {(documents || []).map((document) => (
          <SelectableListItem
            key={document.id}
            className="list-item"
            selected={selected === document.id}
            item={<DocumentBadge item={document} patient={patient} />}
            onClick={() => onSelect(document)}
          />
        ))}
      </List>
    </div>
  );

BatchApproveDocuments.propTypes = {
  documents: PropTypes.array.isRequired,
  patientId: PropTypes.string.isRequired,
};

export const FOLLOW_UP_MESSAGES = [
  {
    id: 'no_message',
    value: 'Generic Message',
    text: '',
    disabled: true,
  },
  {
    id: 'followup_telemedicine',
    value: 'Follow-up Telemedicine',
    text: 'Please book a follow-up telemedicine appointment at your convenience. Your practitioner would like to discuss your results.',
    disabled: true,
    subscriber: true,
  },
  {
    id: 'followup_telemedicine_primary',
    value: 'Follow-up Telemedicine w/ Primary',
    text: 'Please book a telemedicine appointment with your primary NP at your convenience. Your practitioner would like to discuss your results',
    disabled: true,
    subscriber: true,
  },
  {
    id: 'followup_telemedicine_clinic',
    value: 'Follow-up Telemedicine/Clinic',
    text: 'Please book a follow-up clinic or telemedicine appointment at your convenience. Your practitioner would like to discuss your results.',
    disabled: true,
  },
  {
    id: 'followup_clinic',
    value: 'Follow-up Clinic',
    text: 'Please book a follow-up clinic appointment at your convenience. Your practitioner would like to discuss your results.',
    disabled: true,
  },
  {
    id: 'followup_education',
    value: 'Follow-up Health Education',
    text: 'Please book a Health Education appointment with the RPN at your convenience to discuss your results.',
    disabled: true,
    subscriber: true,
  },
  {
    // Minor abnormalities that can wait for the patient's next provider.
    id: 'review_with_new_provider',
    value: 'Review with New Provider',
    // getter: the tenant name comes from portal-data, which isn't loaded when
    // this module is evaluated
    get text() {
      return `Hi, this is ${
        getTenantIdentity().name
      }. Your recent results have been reviewed and don't need urgent attention. Please go over them with your new health care provider at your next visit.`;
    },
    disabled: true,
  },
  {
    id: 'document_notes',
    value: 'See Document Notes',
    text: 'See document notes.',
    disabled: true,
  },
  {
    id: 'custom',
    value: 'Custom Message',
    text: '',
    disabled: false,
  },
];

const ACTIONS = {
  hide: 'hide',
  show: 'show',
};

function getPersistedKey(documents) {
  let key = 'BatchApproveDocuments';
  key += documents
    .map((document) => document.id + getPharmacyRefillAnalysisKey(document))
    .join('_');
  return key;
}

function getPharmacyRefillAnalysisKey(document) {
  if (document.document_type !== DocumentUtils.DOCUMENT_TYPE.pharmacy_authorization) {
    return '';
  }
  return document.pharmacy_authorization_analysis ? '-w_analysis' : '';
}

export default BatchApproveDocuments;
